<script lang="ts">
  /**
   * The admin panel: the owner's way to see who plays and to help somebody
   * back into an account.
   *
   * - **One page with two views**, a list and one player: the shell's
   *   `ListPage` with its title under a back chevron (`docs/decisions/0054`).
   *   The panel is an overlay entry `App.svelte` pushed, so the chevron and
   *   the Back gesture do the same thing: return to wherever the panel was
   *   opened from (`docs/decisions/0047`).
   * - **Rows, not tables.** The player list, a player's facts, their bests
   *   and their matches are all the same borderless groups of rows the rest
   *   of the app uses: a name on the left, a value on the right, a quieter
   *   line under the name. One shape at every width.
   * - **The dangerous things look dangerous.** Deleting an account needs its
   *   username typed, the row that does it is in the danger colour, and then
   *   the shell's `ConfirmDialog` asks once more, in the danger tone, with
   *   focus on the safe answer. Issuing a password says in as many words that
   *   it is shown once.
   *
   * ## Every refusal is a view, not a shrug
   *
   * `admin.access` is the server's answer to "may you be here", and each of
   * its values renders a view that says what happened and offers the way out.
   * None of them is decided here: the panel is told, by a 403 from a route
   * that resolved the session itself.
   */
  import { untrack } from 'svelte';

  import {
    Chip,
    ConfirmDialog,
    Field,
    Group,
    ListPage,
    Note,
    PillButton,
    Row,
    SearchField,
    Snackbar,
  } from '@artibens/app-shell';
  import type { ListSection } from '@artibens/app-shell';
  import { duelVariantName, isRoomVariant } from '@interlock/protocol';

  import {
    admin,
    closePlayer,
    deletePlayer,
    goToPage,
    openPanel,
    openPlayer,
    resetPassword,
    resetPanel,
    search,
  } from '../state/admin.svelte.js';
  import { describeModeKey } from '../state/mode-key.js';

  const { onBack }: { onBack: () => void } = $props();

  /** What the confirmation box currently holds. Never pre-filled. */
  let deleteConfirmation = $state('');
  let askingDelete = $state(false);
  let searchText = $state('');
  /** Bumped on every copy, so the same notice can be shown twice in a row. */
  let copies = $state(0);
  let copiedShown = $state(false);

  /**
   * Open the panel once, on mount, and drop everything on the way out.
   *
   * `untrack` is load-bearing: `openPanel` *reads* `admin.query` and
   * `admin.pageNumber` before its first `await`, so without it this effect
   * depends on them, and typing in the search box would re-run it, whose
   * cleanup calls `resetPanel`, which clears the query just typed.
   */
  $effect(() => {
    untrack(() => {
      void openPanel();
    });
    return () => {
      // A later visit re-reads rather than showing a snapshot of whenever the
      // panel was last open.
      resetPanel();
    };
  });

  /** Debounced, so typing a name is one request rather than one per letter. */
  let lastSearched = '';
  $effect(() => {
    const value = searchText.trim();
    if (value === lastSearched) return undefined;
    const timer = setTimeout(() => {
      lastSearched = value;
      void search(value);
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  });

  const totalPages = $derived(
    admin.page === null ? 1 : Math.max(1, Math.ceil(admin.page.total / admin.page.pageSize)),
  );

  /**
   * Usernames are unique case-insensitively, so the confirmation is too.
   * The friction that matters is having to type the name at all; having to
   * reproduce its capitalisation is a puzzle, not a safeguard.
   */
  const confirmationMatches = $derived(
    admin.detail !== null &&
      deleteConfirmation.trim().toLowerCase() === admin.detail.player.username.toLowerCase(),
  );

  function open(id: string): void {
    deleteConfirmation = '';
    void openPlayer(id);
  }

  function back(): void {
    deleteConfirmation = '';
    askingDelete = false;
    closePlayer();
  }

  async function copyPassword(): Promise<void> {
    const password = admin.temporaryPassword;
    if (password === null) return;
    try {
      await navigator.clipboard.writeText(password);
      copies += 1;
      copiedShown = true;
    } catch {
      // A browser that refuses the clipboard is not worth a message: the
      // password is on the screen in a field that can be selected.
    }
  }

  async function confirmDelete(): Promise<void> {
    askingDelete = false;
    if (!confirmationMatches || admin.detail === null) return;
    if (await deletePlayer(admin.detail.player.id)) deleteConfirmation = '';
  }

  function formatDate(ms: number): string {
    return new Date(ms).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  function formatDateTime(ms: number): string {
    return new Date(ms).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /** `null` is "never", and says so rather than showing an epoch date. */
  function formatLastSeen(ms: number | null): string {
    return ms === null ? 'Never' : formatDate(ms);
  }

  function outcomeLabel(outcome: 'win' | 'loss' | 'draw'): string {
    return outcome === 'win' ? 'Won' : outcome === 'loss' ? 'Lost' : 'Drew';
  }

  function record(value: { wins: number; losses: number; draws: number }): string {
    return `${String(value.wins)}–${String(value.losses)}–${String(value.draws)}`;
  }

  /**
   * "Duel Sprint" rather than `duelSprint`, from the protocol's own table. A
   * stored value this build does not recognise is shown as it is: it still
   * names a real match somebody played.
   */
  function variantLabel(variant: string): string {
    return isRoomVariant(variant) ? duelVariantName(variant) : variant;
  }

  /** Whether the server let this account in: anything else is a refusal, said in the lead. */
  const accessible = $derived(
    admin.access !== 'forbidden' &&
      admin.access !== 'signed-out' &&
      admin.access !== 'unavailable' &&
      admin.access !== 'unknown',
  );

  /** The page's sections: one player's, or the totals and the list; none while a refusal is shown. */
  const sections = $derived.by((): ListSection[] => {
    if (!accessible) return [];
    const detail = admin.detail;
    if (detail !== null) {
      return [
        {
          id: 'facts',
          content: facts,
          note: '"Last seen" is the most recent sign-in or finished match this account has on record. It is not the last time the game was opened, because a saved login needs no new sign-in for a month.',
        },
        { id: 'bests', label: 'Personal bests', content: bests },
        {
          id: 'matches',
          label: 'Recent matches',
          content: matches,
          ...(detail.matches.length > 0
            ? { note: 'The most recent matches, newest first. There may be older ones.' }
            : {}),
        },
        {
          id: 'recovery',
          label: 'Account recovery',
          content: recovery,
          note:
            admin.temporaryPassword !== null && admin.temporaryPassword !== ''
              ? 'Give this to them now. It is shown once and cannot be looked up again. Every device they were signed in on has been signed out.'
              : 'There is no email in this game, so this is the only way back into an account whose password is lost. It sets a new random password, shows it once, and signs that account out everywhere.',
        },
        {
          id: 'delete',
          label: 'Delete this account',
          content: deletion,
          note: 'The account, its saved settings and its personal bests go for good. The matches it played stay, showing the name it played under and no account behind it. This cannot be undone.',
        },
      ];
    }
    const list: ListSection[] = [];
    if (admin.overview) list.push({ id: 'totals', content: totals });
    list.push({ id: 'players', label: 'Players', content: playersList });
    return list;
  });
</script>

{#snippet lead()}
  <!-- Always present, so the live region exists before it has anything to say. -->
  <p class="working" aria-live="polite">{admin.busy ? 'Working…' : ''}</p>

  {#if admin.access === 'forbidden'}
    <section class="refusal" data-testid="admin-forbidden">
      <h2 class="heading">Not an administrator</h2>
      <Note>
        This account is not an administrator, so there is nothing here for it. Administrators are
        named in the server's <code>INTERLOCK_ADMIN_USERNAMES</code> setting, which only somebody with
        access to the server can change.
      </Note>
      <PillButton label="Back to the game" onclick={onBack} />
    </section>
  {:else if admin.access === 'signed-out'}
    <section class="refusal">
      <h2 class="heading">Signed out</h2>
      <Note>
        This session is no longer valid. It may have expired, or been signed out somewhere else.
        Sign in again from the profile sheet.
      </Note>
      <PillButton label="Back" onclick={onBack} />
    </section>
  {:else if admin.access === 'unavailable'}
    <section class="refusal">
      <h2 class="heading">Accounts are unavailable</h2>
      <Note>
        This server is running without accounts, or cannot reach them right now, so there is nothing
        to look after here. The game itself is not affected.
      </Note>
      <PillButton label="Back to the game" onclick={onBack} />
    </section>
  {:else if admin.access === 'unknown'}
    <!-- Nothing has been answered yet. Either the first request is still out,
         or it failed for a reason that is not about permission (a dead
         network, a rate limit) and there has to be a way to try again. -->
    <section class="refusal">
      {#if admin.busy}
        <Note>Loading…</Note>
      {:else}
        <h2 class="heading">Could not load the panel</h2>
        <Note>
          The server did not answer. That is usually the connection rather than the account. Nothing
          has been changed.
        </Note>
        <PillButton label="Try again" onclick={() => void openPanel()} />
      {/if}
    </section>
  {:else if admin.detail}
    <!-- One player -->
    <div class="detail-head">
      <PillButton variant="plain" label="All players" testid="admin-all-players" onclick={back} />
      {#if admin.detail.isAdmin}
        <Chip tone="accent">Administrator</Chip>
      {/if}
    </div>
    <p class="username" data-testid="detail-username">{admin.detail.player.username}</p>
  {:else if admin.notice}
    <Note tone="ink" status testid="admin-notice">{admin.notice}</Note>
  {/if}

  {#if admin.error}
    <Note tone="error">{admin.error}</Note>
  {/if}
{/snippet}

{#snippet facts()}
  {#if admin.detail}
    <Group>
      <Row label="Registered" value={formatDate(admin.detail.player.createdAtMs)} />
      <Row label="Last seen" value={formatLastSeen(admin.detail.summary.lastSeenAtMs)} />
      <Row label="Personal bests" value={admin.detail.summary.bestCount} />
      <Row label="Won–lost–drawn" value={record(admin.detail.summary.record)} />
    </Group>
  {/if}
{/snippet}

{#snippet bests()}
  {#if admin.detail}
    <Group>
      {#each admin.detail.bests as best (best.modeKey)}
        <Row
          label={describeModeKey(best.modeKey)}
          below={formatDate(best.achievedAtMs)}
          value={best.formatted}
        />
      {/each}
      {#if admin.detail.bests.length === 0}
        <Row label="No bests recorded yet." tone="muted" />
      {/if}
    </Group>
  {/if}
{/snippet}

{#snippet matches()}
  {#if admin.detail}
    <Group>
      {#each admin.detail.matches as match (match.matchId)}
        <Row
          label={`${variantLabel(match.variant)} vs ${match.opponents.join(', ') || 'nobody'}`}
          below={`${formatDateTime(match.endedAtMs)} · ${match.roomCode}`}
          value={outcomeLabel(match.outcome)}
        />
      {/each}
      {#if admin.detail.matches.length === 0}
        <Row label="No duels played yet." tone="muted" />
      {/if}
    </Group>
  {/if}
{/snippet}

{#snippet recovery()}
  {#if admin.detail}
    {#if admin.temporaryPassword}
      <div class="field-block">
        <Field
          label="Temporary password"
          value={admin.temporaryPassword}
          readonly
          testid="temporary-password"
        />
      </div>
      <Group>
        <Row label="Copy" tone="action" onclick={() => void copyPassword()} />
      </Group>
    {:else}
      <Group>
        <Row
          label="Issue a temporary password"
          tone="action"
          disabled={admin.busy}
          onclick={() => void resetPassword(admin.detail?.player.id ?? '')}
        />
      </Group>
    {/if}
  {/if}
{/snippet}

{#snippet deletion()}
  {#if admin.detail}
    <div class="field-block">
      <Field
        label={`To confirm, type the username: ${admin.detail.player.username}`}
        autocapitalize="none"
        autocomplete="off"
        bind:value={deleteConfirmation}
        testid="delete-confirmation"
      />
    </div>
    <Group>
      <Row
        label={`Delete ${admin.detail.player.username}`}
        tone="danger"
        disabled={!confirmationMatches || admin.busy}
        opensDialog
        testid="delete-account"
        onclick={() => {
          askingDelete = true;
        }}
      />
    </Group>
  {/if}
{/snippet}

{#snippet totals()}
  {#if admin.overview}
    <Group name="Totals">
      <Row label="Players" value={admin.overview.players} valueTestid="stat-players" />
      <Row label="Matches" value={admin.overview.matches} />
      <Row label="Today" value={admin.overview.matchesToday} />
      <Row label="Live rooms" value={admin.overview.liveRooms} />
    </Group>
  {/if}
{/snippet}

{#snippet searchBox()}
  <SearchField
    bind:value={searchText}
    label="Find a player by name"
    maxlength={20}
    testid="player-search"
  />
{/snippet}

{#snippet playersList()}
  {#if admin.page === null || admin.page.players.length === 0}
    <Group>
      <Row
        label={admin.query === '' ? 'No accounts yet.' : `Nobody matches “${admin.query}”.`}
        tone="muted"
        testid="players-empty"
      />
      {#if admin.query !== ''}
        <Row
          label="Show everybody"
          tone="action"
          onclick={() => {
            searchText = '';
          }}
        />
      {/if}
    </Group>
  {:else}
    <Group>
      {#each admin.page.players as player (player.id)}
        <Row
          label={player.username}
          below={`Registered ${formatDate(player.createdAtMs)} · ${String(player.bestCount)} ${player.bestCount === 1 ? 'best' : 'bests'} · Last seen ${formatLastSeen(player.lastSeenAtMs)}`}
          value={record(player.record)}
          chevron
          testid="player-row"
          onclick={() => {
            open(player.id);
          }}
        />
      {/each}
    </Group>

    {#if totalPages > 1}
      <div class="pager">
        <PillButton
          variant="light"
          label="Previous"
          disabled={admin.pageNumber <= 1 || admin.busy}
          onclick={() => void goToPage(admin.pageNumber - 1)}
        />
        <Note size="sm">Page {admin.pageNumber} of {totalPages}</Note>
        <PillButton
          variant="light"
          label="Next"
          disabled={admin.pageNumber >= totalPages || admin.busy}
          onclick={() => void goToPage(admin.pageNumber + 1)}
        />
      </div>
    {/if}
    <p class="note">
      {admin.page.total}
      {admin.page.total === 1 ? 'account' : 'accounts'}. The figure at the right is won, lost and
      drawn. Pick one to see their bests, their match history and the two things you can do to it.
    </p>
  {/if}
{/snippet}

<!-- The page reaches the top edge, so its titled header clears the notch
     (`safeArea`, shell 1.3.0). -->
<ListPage
  title="Admin"
  onback={onBack}
  safeArea
  {lead}
  search={accessible && admin.detail === null ? searchBox : undefined}
  searchAfter="totals"
  {sections}
  testid="admin-screen"
/>

<!--
  The second half of the delete: the username has been typed, and this asks
  once more. Escape, Back and the backdrop are all "Keep the account", and
  focus starts there.
-->
<ConfirmDialog
  open={askingDelete && admin.detail !== null}
  title={`Delete ${admin.detail?.player.username ?? 'this account'}?`}
  body="The account, its settings and its personal bests go for good. This cannot be undone."
  confirmLabel="Delete for good"
  cancelLabel="Keep the account"
  tone="danger"
  testid="delete-confirm"
  onconfirm={() => void confirmDelete()}
  oncancel={() => {
    askingDelete = false;
  }}
/>

{#if copiedShown}
  {#key copies}
    <Snackbar
      message="Copied"
      testid="copied-notice"
      ondone={() => {
        copiedShown = false;
      }}
    />
  {/key}
{/if}

<style>
  .working {
    margin: 0;
    min-height: 1.1rem;
    text-align: center;
    font: var(--ui-text-label);
    color: var(--ui-ink-2);
  }

  /* A refusal: what happened, and the way out. */
  .refusal {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--ui-space-3);
    padding: var(--ui-space-4);
    border-radius: var(--ui-radius-md);
    background: var(--ui-card);
  }

  .heading {
    margin: 0;
    font: var(--ui-text-heading);
    color: var(--ui-ink);
  }

  .detail-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ui-space-3);
    flex-wrap: wrap;
  }

  .username {
    margin: 0 var(--ui-space-1);
    font: var(--ui-text-title);
    color: var(--ui-ink);
    overflow-wrap: anywhere;
  }

  .field-block {
    margin: 0 0 var(--ui-space-3);
  }

  .pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ui-space-3);
    flex-wrap: wrap;
    margin-top: var(--ui-space-3);
  }

  /* The list's closing sentence, at the template's note inset. */
  .note {
    margin: var(--ui-space-1) var(--ui-space-1) 0;
    font: var(--ui-text-caption);
    font-weight: 400;
    line-height: 1.4;
    color: var(--ui-ink-2);
  }

  /* A setting's name, in the house face like every other word. */
  code {
    font: inherit;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
</style>
